// Pack identifiers and the hardcoded PACKS const. v0.1 has two packs and that
// is on purpose — see the "Don't abstract until N=3" principle in CLAUDE.md.
//
// When v0.2 adds a third pack, extend PackId, add an entry to PACKS, and let
// TypeScript's exhaustiveness checks point at every site that needs touching.

export type PackId = 'joseph' | 'nativity'

export const PACK_IDS = ['joseph', 'nativity'] as const satisfies readonly PackId[]

export interface PackItemDetail {
  /** YouTube watch URL (e.g. https://youtu.be/rkGDGePrM1M) or null when no
   *  per-item story video is available yet. ItemDetailPage tab A renders a
   *  16:9 iframe when this is non-null; otherwise it shows a "故事影片製作中"
   *  placeholder. v0.1 ships only joseph.coat with a real URL. */
  videoUrl: string | null
}

export interface PackMeta {
  id: PackId
  nameZh: string
  nameEn: string
  scriptureRange: string
  /** Item ids in display order — drives the grid scene layout. */
  itemIds: readonly string[]
  /**
   * Pack-level background story rendered above the items grid on PackHomePage.
   * Round-1 feedback #4: "補一個背景故事區，將故事完整敘述一遍". Max 500 chars
   * (enforced in packs.test.ts). Null hides the section entirely — no empty
   * container. Content is filled by future commits; v0.1 ships both as null
   * so the schema + UI container land first.
   */
  storyZh: string | null
  storyEn: string | null
  /**
   * Per-item detail-page data (currently just videoUrl). Indexed by item id.
   * NOT typed as Record<string, PackItemDetail> — the concrete pack types use
   * a literal-key map so TypeScript exhaustive-checks every itemId in itemIds
   * has a matching entry at compile time.
   */
  itemDetails: Record<string, PackItemDetail>
  /** Bonus full-scene reward unlocked when all items are colored. */
  bonus: {
    id: string
    nameZh: string
    nameEn: string
    scriptureRef: string
  }
}

export const PACKS = {
  joseph: {
    id: 'joseph',
    nameZh: '約瑟的彩衣',
    nameEn: "Joseph's Coat of Many Colors",
    scriptureRange: '創世記 37-50',
    itemIds: ['coat', 'dream', 'silver-cup', 'granary', 'reunion'],
    storyZh: null,
    storyEn: null,
    itemDetails: {
      // Round-1 feedback #5: per-item story videos. Joseph's coat is the
      // first one to ship a real YouTube URL (主公-provided demo). Remaining
      // items default to null and ItemDetailPage shows a "故事影片製作中"
      // placeholder until subsequent commits fill them in.
      coat: { videoUrl: 'https://youtu.be/rkGDGePrM1M' },
      dream: { videoUrl: null },
      'silver-cup': { videoUrl: null },
      granary: { videoUrl: null },
      reunion: { videoUrl: null },
    },
    bonus: {
      // Snake-case is intentional — bonus IDs are the only exception in the
      // codebase. The underscore separates the pack from the scene id so
      // `bonusAssetUrl` can split deterministically (silver-cup uses hyphens
      // inside the item id, so a hyphen separator would be ambiguous).
      id: 'joseph_panorama',
      nameZh: '約瑟與雅各重逢',
      nameEn: 'Joseph reuniting with Jacob',
      scriptureRef: '創 46:29-30',
    },
  },
  nativity: {
    id: 'nativity',
    nameZh: '耶穌降生',
    nameEn: 'The Nativity',
    scriptureRange: '路加 1-2, 馬太 1-2',
    itemIds: ['angel', 'mary', 'manger', 'star', 'magi', 'shepherds', 'baby-jesus'],
    storyZh: null,
    storyEn: null,
    itemDetails: {
      angel: { videoUrl: null },
      mary: { videoUrl: null },
      manger: { videoUrl: null },
      star: { videoUrl: null },
      magi: { videoUrl: null },
      shepherds: { videoUrl: null },
      'baby-jesus': { videoUrl: null },
    },
    bonus: {
      id: 'nativity_panorama',
      nameZh: '伯利恆夜景全景',
      nameEn: 'Bethlehem Night Panorama',
      scriptureRef: '路 2:1-20, 太 2:1-12',
    },
  },
} as const satisfies Record<PackId, PackMeta>

export function isPackId(value: unknown): value is PackId {
  return typeof value === 'string' && (PACK_IDS as readonly string[]).includes(value)
}

// Single discriminator for "is this itemId actually the bonus full-scene id?".
// Used by ColoringPage routing and the open-endpoint guard so neither side
// re-implements `=== PACKS[packId].bonus.id` and they cannot drift.
export function isBonusId(itemId: string, packId: PackId): boolean {
  return itemId === PACKS[packId].bonus.id
}
